import { useCallback, useState } from 'react'
import { GridApi } from 'ag-grid-community'
import {
  CriticalPathLinkData,
  CriticalPathLinkUpdates,
  useCriticalPathContext,
} from './CriticalPathContext'
import {
  getWbsItemDisplayName,
  getWbsItemIconUrl,
  getWbsItemScheduledDate,
} from '../utils/linkInfoPopupUtils'
import { GanttRowItem } from '../../GanttRowItem'
import { LinkEditorResult } from '../WbsItemLink/LinkEditorPopup'

export interface CpPopupState {
  link: CriticalPathLinkData
  fromName: string
  toName: string
  fromIconUrl?: string
  toIconUrl?: string
  fromProjectName?: string
  toProjectName?: string
  fromProjectIconUrl?: string
  toProjectIconUrl?: string
  fromDateRange: { min: Date; max: Date } | null
  toDateRange: { min: Date; max: Date } | null
}

const toDateRange = (
  scheduled: { startDate?: string; endDate?: string } | undefined
): { min: Date; max: Date } | null => {
  if (!scheduled?.startDate || !scheduled?.endDate) return null
  return {
    min: new Date(scheduled.startDate),
    max: new Date(scheduled.endDate),
  }
}

export const useCriticalPathLinePopup = (
  api: GridApi | undefined,
  dataPath?: string,
  getGanttRowItem?: (data: unknown) => GanttRowItem | undefined
) => {
  const criticalPathContext = useCriticalPathContext()
  const [cpPopup, setCpPopup] = useState<CpPopupState | null>(null)

  const handleLineClick = useCallback(
    (link: CriticalPathLinkData, event: React.MouseEvent) => {
      const prefix = dataPath ? `${dataPath}.` : ''
      const uuidMap = criticalPathContext?.wbsItemUuidToRowIndex
      const infoMap = criticalPathContext?.wbsItemInfoMap
      const fromRow = uuidMap?.get(link.fromWbsItemUuid) ?? link.fromRowIndex
      const toRow = uuidMap?.get(link.toWbsItemUuid) ?? link.toRowIndex

      // グリッドの行データから名前・アイコンを取得し、取得できなければ wbsItemInfoMap にフォールバック
      const fromName =
        (api ? getWbsItemDisplayName(api, fromRow, prefix) : '') ||
        infoMap?.get(link.fromWbsItemUuid)?.displayName ||
        ''
      const toName =
        (api ? getWbsItemDisplayName(api, toRow, prefix) : '') ||
        infoMap?.get(link.toWbsItemUuid)?.displayName ||
        ''

      const fromScheduled = api
        ? getWbsItemScheduledDate(api, fromRow, prefix, getGanttRowItem)
        : undefined
      const toScheduled = api
        ? getWbsItemScheduledDate(api, toRow, prefix, getGanttRowItem)
        : undefined

      const fromIconUrl =
        (api ? getWbsItemIconUrl(api, fromRow, prefix) : undefined) ??
        infoMap?.get(link.fromWbsItemUuid)?.iconUrl
      const toIconUrl =
        (api ? getWbsItemIconUrl(api, toRow, prefix) : undefined) ??
        infoMap?.get(link.toWbsItemUuid)?.iconUrl
      // プロジェクト名・アイコンはCP取得レスポンス由来の infoMap から解決する
      const fromInfo = infoMap?.get(link.fromWbsItemUuid)
      const toInfo = infoMap?.get(link.toWbsItemUuid)
      setCpPopup({
        link,
        fromName,
        toName,
        fromIconUrl,
        toIconUrl,
        fromProjectName: fromInfo?.projectName,
        toProjectName: toInfo?.projectName,
        fromProjectIconUrl: fromInfo?.projectIconUrl,
        toProjectIconUrl: toInfo?.projectIconUrl,
        fromDateRange: toDateRange(fromScheduled),
        toDateRange: toDateRange(toScheduled),
      })
    },
    [api, dataPath, criticalPathContext, getGanttRowItem]
  )

  const handleDelete = useCallback(() => {
    if (cpPopup && criticalPathContext) {
      criticalPathContext.removeCriticalPathLink(cpPopup.link.uuid)
    }
    setCpPopup(null)
  }, [cpPopup, criticalPathContext])

  const handleUpdate = useCallback(
    (result: LinkEditorResult) => {
      if (!cpPopup || !criticalPathContext) return

      // Apply date changes
      if (result.fromDate) {
        criticalPathContext.updateCriticalPathLinkDate(
          cpPopup.link.uuid,
          'fromDate',
          result.fromDate
        )
      }
      if (result.toDate) {
        criticalPathContext.updateCriticalPathLinkDate(
          cpPopup.link.uuid,
          'toDate',
          result.toDate
        )
      }

      // Apply color/description changes
      const propUpdates: CriticalPathLinkUpdates = {}
      if (result.color !== undefined) propUpdates.color = result.color
      if (result.description !== undefined) {
        propUpdates.description = result.description
      }
      if (Object.keys(propUpdates).length > 0) {
        criticalPathContext.updateCriticalPathLinkProps(
          cpPopup.link.uuid,
          propUpdates
        )
      }

      setCpPopup(null)
    },
    [cpPopup, criticalPathContext]
  )

  const closePopup = useCallback(() => setCpPopup(null), [])

  // Open popup with pre-resolved info (for fallback links where one endpoint is hidden)
  const openPopup = useCallback((state: CpPopupState) => setCpPopup(state), [])

  return {
    cpPopup,
    handleLineClick,
    handleDelete,
    handleUpdate,
    closePopup,
    openPopup,
  }
}
